import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { formatFileSize, truncateFilename } from "@utils:file";
import {
  DragEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { MdCloudUpload } from "react-icons/md";

import { SIZE_CONFIGS } from "./config";
import { UploadZoneProps } from "./types";

/**
 * Unified upload zone component with consistent styling across the application.
 * Supports drag-drop, click to browse, and file preview.
 */
export const UploadZone = ({
  onFileSelect,
  selectedFile,
  accept,
  disabled = false,
  size = "default",
  title,
  subtitle,
  hint,
  previewUrl,
  fileIcon,
  sx,
}: UploadZoneProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [generatedPreviewUrl, setGeneratedPreviewUrl] = useState<string | null>(
    null,
  );

  const config = SIZE_CONFIGS[size];

  // Generate preview URL for image files
  useEffect(() => {
    if (selectedFile && selectedFile.type.startsWith("image/") && !previewUrl) {
      const url = URL.createObjectURL(selectedFile);
      setGeneratedPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setGeneratedPreviewUrl(null);
    }
  }, [selectedFile, previewUrl]);

  const displayPreviewUrl = previewUrl || generatedPreviewUrl;
  const hasFile = selectedFile || previewUrl;

  // Drag event handlers
  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setIsDragging(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        onFileSelect(files[0]);
      }
    },
    [disabled, onFileSelect],
  );

  // Click handler
  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  // File input change handler
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        onFileSelect(files[0]);
      }
      // Reset input to allow same file selection
      e.target.value = "";
    },
    [onFileSelect],
  );

  // Base styles
  const baseStyles = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: config.padding,
    minHeight: config.minHeight,
    border: `${config.borderWidth} dashed`,
    borderColor: isDragging
      ? theme.palette.primary.dark
      : theme.palette.primary.main,
    borderRadius: config.borderRadius,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, isDragging ? 0.06 : 0.03)} 0%, ${alpha(theme.palette.primary.main, isDragging ? 0.1 : 0.06)} 100%)`,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    opacity: disabled ? 0.6 : 1,
    position: "relative",
    overflow: "hidden",
    // Radial gradient overlay
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 50% 50%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 70%)`,
      opacity: 0,
      transition: "opacity 0.4s ease",
      pointerEvents: "none",
    },
    // Hover state - shadow only, no lift
    "&:hover": disabled
      ? {}
      : {
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          borderColor: theme.palette.primary.dark,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
        },
    "&:hover::before": disabled ? {} : { opacity: 1 },
  };

  // Render empty state content
  const renderEmptyState = () => (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={0.5}
      sx={{ width: "100%" }}
    >
      {/* Custom file icon or default upload icon */}
      {fileIcon ? (
        <Box
          sx={{
            mb: 1,
            alignSelf: "stretch",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {fileIcon}
        </Box>
      ) : (
        <Box
          sx={{
            color: theme.palette.primary.main,
            mb: 1,
          }}
        >
          <MdCloudUpload size={config.iconSize} />
        </Box>
      )}
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: config.titleSize,
          color: theme.palette.text.primary,
          textAlign: "center",
        }}
      >
        {title ?? t("components.dropFileHere")}
      </Typography>
      <Typography
        sx={{
          fontSize: config.subtitleSize,
          color: theme.palette.primary.main,
          fontWeight: 500,
          textAlign: "center",
        }}
      >
        {subtitle ?? t("components.orClickToBrowse")}
      </Typography>
      {hint && (
        <Typography
          sx={{
            fontSize: config.hintSize,
            color: theme.palette.text.secondary,
            textAlign: "center",
            mt: 0.5,
          }}
        >
          {hint}
        </Typography>
      )}
    </Stack>
  );

  // Render file preview state
  const renderFilePreview = () => (
    <Stack alignItems="center" justifyContent="center" spacing={1}>
      {/* Preview icon or image */}
      <Box
        sx={{
          width: size === "compact" ? 40 : 64,
          height: size === "compact" ? 40 : 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {displayPreviewUrl && (
          <Box
            component="img"
            src={displayPreviewUrl}
            alt={t("components.previewAlt")}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        )}
        {!displayPreviewUrl && fileIcon && (
          <Box sx={{ color: theme.palette.primary.main }}>{fileIcon}</Box>
        )}
        {!displayPreviewUrl && !fileIcon && (
          <MdCloudUpload
            size={size === "compact" ? 20 : 32}
            color={theme.palette.primary.main}
          />
        )}
      </Box>

      {/* File name - single line, truncate middle to preserve extension */}
      {selectedFile && (
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: config.titleSize,
            color: theme.palette.text.primary,
            textAlign: "center",
            maxWidth: "90%",
            whiteSpace: "nowrap",
          }}
        >
          {truncateFilename(selectedFile.name, 30)}
        </Typography>
      )}

      {/* File size */}
      {selectedFile && (
        <Typography
          sx={{
            fontSize: config.subtitleSize,
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          {formatFileSize(selectedFile.size)}
        </Typography>
      )}

      {/* Change hint */}
      <Typography
        sx={{
          fontSize: config.hintSize,
          color: theme.palette.primary.main,
          fontWeight: 500,
          mt: size === "compact" ? 0 : 0.5,
        }}
      >
        {t("components.clickToChangeFile")}
      </Typography>
    </Stack>
  );

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={
          hasFile ? t("components.changeFile") : t("components.uploadFile")
        }
        aria-disabled={disabled}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        sx={[baseStyles, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {hasFile ? renderFilePreview() : renderEmptyState()}
      </Box>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        style={{ display: "none" }}
        disabled={disabled}
        aria-hidden="true"
      />
    </>
  );
};
